import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const DealManagement = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchDeals();
  }, [statusFilter]);

  const fetchDeals = async () => {
    setLoading(true);
    try {
      let query = supabase?.from('deals')?.select(`
          *,
          buyer:user_profiles!deals_buyer_id_fkey(full_name, email),
          traveler:user_profiles!deals_traveler_id_fkey(full_name, email),
          request:purchase_requests(item_name, category)
        `)?.order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query?.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (!error) {
        setDeals(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/10 text-green-600 border-green-500/20';
      case 'in_transit': return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      case 'accepted': return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
      case 'cancelled': return 'bg-red-500/10 text-red-600 border-red-500/20';
      default: return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Deal Management</h2>
          <p className="text-muted-foreground">Monitor and manage all platform transactions</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e?.target?.value)}
            className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Status</option>
            <option value="accepted">Accepted</option>
            <option value="in_transit">In Transit</option>
            <option value="delivered">Delivered</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {deals?.map((deal) => (
            <div key={deal?.id} className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-foreground">{deal?.request?.item_name}</h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(deal?.status)}`}>
                      {deal?.status}
                    </span>
                    {deal?.is_ai_matched && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-500/10 text-purple-600 border border-purple-500/20">
                        <Icon name="Sparkles" size={12} />
                        AI Matched
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">Category: {deal?.request?.category}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Created</p>
                  <p className="text-sm text-foreground">{new Date(deal?.created_at)?.toLocaleDateString()}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Buyer</p>
                  <div className="flex items-center gap-2">
                    <Icon name="User" size={16} className="text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{deal?.buyer?.full_name}</p>
                      <p className="text-xs text-muted-foreground">{deal?.buyer?.email}</p>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Traveler</p>
                  <div className="flex items-center gap-2">
                    <Icon name="Plane" size={16} className="text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{deal?.traveler?.full_name}</p>
                      <p className="text-xs text-muted-foreground">{deal?.traveler?.email}</p>
                    </div>
                  </div>
                </div>
              </div>

              {deal?.ai_match_score && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">AI Match Score</span>
                    <span className="text-sm font-semibold text-purple-600">{(deal?.ai_match_score * 100)?.toFixed(0)}%</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DealManagement;